import {useCallback, useEffect, useRef, useState} from 'react';
import {Popup} from 'react-leaflet';
import {CaregiverObject, Location, ScheduledStop} from 'src/services/api/types/types';
import {XIcon} from 'src/components/icons/XIcon';
import {useMapDataContext} from 'src/context/MapDataContext';
import {useTranslation} from 'react-i18next';
import {fetchScheduledStopsAtLocation} from 'src/services/api/api';
import {ScheduledStops} from 'src/components/main/components/ScheduledStops';
import {parse, isValid, format, addDays} from 'date-fns';
import {useSearchParams} from 'react-router-dom';

interface LocationPopupProps {
    location: Location;
    isOpenId: boolean;
    getDistanceToLocation?: {value: number; unit: string};
}

const baseUrl = process.env.REACT_APP_CLINIC_API_ENDPOINT;

export function LocationPopup({location, isOpenId, getDistanceToLocation}: LocationPopupProps) {
    const {t} = useTranslation();

    const popup = useRef<L.Popup>(null);

    const {state: mapDataState} = useMapDataContext();

    const caregiverName = isOpenId
        ? mapDataState.careGivers.find((caregiver: CaregiverObject) => caregiver.id === location.careGiver)?.label
        : '';

    const close = () => {
        if (popup.current) {
            popup.current.close();
        }
    };

    const getPossibleBookingOptions = () => {
        if (location.hasDropin && location.hasTimebook) {
            return t('clinic_popup.booking_options.both');
        }
        if (location.hasDropin) {
            return t('clinic_popup.booking_options.dropin');
        }
        if (location.hasTimebook) {
            return t('clinic_popup.booking_options.timebook');
        }
    };

    const parseDateString = useCallback((value: string) => {
        const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
        return isValid(parsedDate) ? parsedDate : null;
    }, []);
    const [urlSearchParams] = useSearchParams();
    const today = format(new Date(), 'yyyy-MM-dd');
    const defaultTo = format(addDays(new Date(), 30), 'yyyy-MM-dd');
    const from = parseDateString(urlSearchParams.get('from') ?? today) ?? new Date();
    const to =
        parseDateString(urlSearchParams.get('to') ?? defaultTo) ?? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

    const [scheduledStops, setScheduledStops] = useState<Array<ScheduledStop>>([]);

    useEffect(() => {
        if (!isOpenId) return;

        const loadSchedule = async () => {
            const stops = await fetchScheduledStopsAtLocation(location.id, from, to);
            setScheduledStops(stops.slice(0, 3));
        };

        loadSchedule();
    }, [location, isOpenId]);

    const logoUrl = isOpenId ? `${baseUrl}/v1/careGivers/${location.careGiver}/logo` : '';

    const locationDetailsUrl = '/plats';

    return (
        <Popup
            autoPanPadding={[15, 15]}
            ref={popup}
            maxWidth={343}
            minWidth={343}
            closeButton={false}
            className='pop-up'
            closeOnClick={false}
        >
            <div tabIndex={-1} className='popup-header'>
                <button
                    className='close-button'
                    aria-label={`${t('button_close.text')} ${location.name}`}
                    onClick={() => close()}
                >
                    <XIcon />
                </button>
                <span>
                    {logoUrl && <img src={logoUrl} alt={caregiverName} />}
                    {!logoUrl && <>{caregiverName}</>}
                </span>
            </div>
            <div className='popup-main'>
                <h3 className='popup-main__header'>{location.name}</h3>
                <div
                    className={`popup-main__text-container${
                        location.address && !getDistanceToLocation ? ' popup-main__text-container--flex-column' : ''
                    }`}
                >
                    <p className='popup-main__text-container__text'>
                        {getDistanceToLocation && (
                            <>
                                <span className='screen-reader-only'>
                                    {t('distance_to_clinic.arialabel', getDistanceToLocation)}
                                </span>
                                <span
                                    aria-hidden
                                >{`${getDistanceToLocation.value} ${getDistanceToLocation.unit} •`}</span>
                            </>
                        )}
                        <span aria-hidden>{getDistanceToLocation && <>&nbsp;</>}</span>
                        {location.address && `${location.address}, ${location.city}`}
                    </p>
                    <p className='popup-main__text-container__text'>{getPossibleBookingOptions()}</p>
                </div>
            </div>
            <div className='popup-extra'>
                <ScheduledStops scheduledStops={scheduledStops} includeInfo={false} />
            </div>
            <div className='popup-button'>
                <a
                    href={
                        location.externalBookingUrl
                            ? location.externalBookingUrl
                            : `${locationDetailsUrl}/${location.id}`
                    }
                    className='btn btn--primary'
                    target='_blank'
                    rel='noreferrer'
                >
                    {location.externalBookingUrl && location.externalBookingUrlType === 'caregiver'
                        ? t('list_clinics_to_caregiver.text')
                        : t('location_popup.button_location.text')}
                </a>
            </div>
        </Popup>
    );
}
